import { z } from "zod";

// Cap on how many saved tests a single user can have on the system at once.
// Enforced server-side (POST /api/tests, POST /api/tests/:id/duplicate) and
// surfaced proactively on the Dashboard's "Create New Test" button.
export const MAX_TESTS_PER_USER = 10;

// Map the user-facing readingLevel string to the numeric grade Blacksmith
// expects ("K" → 0, "College" → 13, "Graduate" → 17, "Adult / workforce" →
// 12, "1"–"12" → 1–12). Single source of truth for both server
// (generation/regeneration) and client (regenerate request bodies).
// Defaults to 7 when the value is missing or unrecognized — same as the
// server-side defaults that pre-date this helper.
export function readingLevelToGrade(level: string | undefined | null): number {
  if (!level) return 7;
  if (level === "K") return 0;
  if (level === "College") return 13;
  if (level === "Graduate") return 17;
  if (level === "Adult / workforce") return 12;
  const parsed = parseInt(level, 10);
  return Number.isFinite(parsed) ? parsed : 7;
}

// User-facing display text for a readingLevel value. Surfaces show the
// label, never the numeric grade ("Adult / workforce", not 12). Defaults to
// "Grade 7" for missing or unrecognized values, matching readingLevelToGrade.
export function readingLevelDisplayLabel(level: string | undefined | null): string {
  if (!level) return "Grade 7";
  if (level === "K") return "Kindergarten";
  if (level === "College") return "College";
  if (level === "Graduate") return "Graduate";
  if (level === "Adult / workforce") return "Adult / workforce";
  // Exact "1"–"12" only: parseInt would accept numeric prefixes ("8bogus",
  // "01", "12th"), which are unrecognized levels and must fall back.
  if (/^(?:[1-9]|1[0-2])$/.test(level)) return `Grade ${level}`;
  return "Grade 7";
}

// Source anchor: where a piece of content came from inside an uploaded file.
// Surfaced to the user as a tooltip next to topic names on the blueprint.
// `source` (filename) is filled in by the client when resolving markers, not
// by the extractor — the extractor only knows the location WITHIN a file.
export const sourceAnchorSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("pdf-page"), page: z.number().int().min(1), source: z.string().optional() }),
  z.object({ kind: z.literal("pptx-slide"), slide: z.number().int().min(1), source: z.string().optional() }),
  z.object({ kind: z.literal("docx-heading"), heading: z.string(), source: z.string().optional() }),
]);
export type SourceAnchor = z.infer<typeof sourceAnchorSchema>;

// Accepted lesson-material upload formats. Single source of truth shared by
// the client (file picker, client-side validation) and the server (multer
// filter, extraction) so the two sides cannot drift apart again. Legacy
// Office 97-2003 formats (.doc/.ppt) are deliberately absent: neither has a
// working extractor, so they would only ever fail with a confusing 422.
export const ACCEPTED_FILE_EXTENSIONS = [".pdf", ".docx", ".txt", ".pptx"] as const;

// Cap on extracted/pasted lesson-material text. Single source of truth shared
// by the server (extraction sampling in upload.ts) and the client (text
// material budget + truncation badges on IdentifyContent) so a material the
// client prepares is never larger than what the server will accept.
export const MAX_EXTRACTED_CHARS = 200_000;

// Global JSON body cap for the API. Must fit the worst valid lesson-material
// payload a source-bearing route can receive: four materials at 200K chars
// each serialized as UTF-8 Unicode (800K chars x up to 4 bytes = 3.2MB) plus
// JSON structure overhead, with headroom. Raising this is what lets
// /api/questions/score and /api/questions/regenerate accept retained lesson
// text; the per-route 50MB parsers cannot override a global parser that runs
// first. Mirrors the 1mb default's conservative intent, scaled to the cap.
export const MAX_JSON_BODY_BYTES = 5 * 1024 * 1024;
export const ACCEPTED_MIME_TYPES = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "text/plain",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  // Browsers without a registered Office handler report OOXML files as one
  // of these generic types; extraction resolves them by file extension.
  "application/octet-stream",
  "application/vnd.ms-office",
] as const;

// One chunk of content from an uploaded file, tagged with where it came
// from. PDFs produce one per page, PPTX one per slide, DOCX one per heading
// section. Anchorless segments (TXT) carry just the text.
export const contentSegmentSchema = z.object({
  text: z.string(),
  anchor: sourceAnchorSchema.optional(),
});
export type ContentSegment = z.infer<typeof contentSegmentSchema>;

// Material Schema (for files and URLs)
export const materialSchema = z.object({
  id: z.string(),
  type: z.enum(["file", "url", "text"]),
  name: z.string(),
  url: z.string().optional(),
  filename: z.string().optional(),
  mimetype: z.string().optional(),
  size: z.number().optional(),
  path: z.string().optional(),
  // Persisted so that file uploads (and their truncation state) survive
  // navigation back to the identify-content page.
  content: z.string().optional(),
  truncated: z.boolean().optional(),
  originalLength: z.number().optional(),
  // SHA-256 of the original file bytes, hex-encoded. Used for dedup.
  hash: z.string().optional(),
  // Per-page / per-slide / per-section breakdown of the file's content.
  // Used to map AI-returned topic excerpts back to source locations for
  // the Content Alignment tooltip on the blueprint page.
  segments: z.array(contentSegmentSchema).optional(),
  // Opaque server-side upload id (returned by POST /api/upload). Used by
  // the preview dialog to fetch original file bytes from
  // GET /api/uploads/:uploadId for visual previews.
  uploadId: z.string().optional(),
  // For url materials: cached result of the server-side accessibility
  // probe + when it was checked. Persisting lets us skip re-probing
  // on every page mount as long as the cached value is still fresh
  // (see URL_STATUS_TTL_MS on the client).
  urlStatus: z.enum(['checking', 'public', 'login_required', 'unreachable', 'unsupported_provider']).optional(),
  urlStatusCheckedAt: z.number().optional(),
});

export type Material = z.infer<typeof materialSchema>;

// Raw lesson text a test was built from, for BCE's Content-Appropriate Stem
// check. Concatenates each material's extracted text — file/pasted content
// for "file"/"text", page text from segments (not the URL) for "url" — and
// returns undefined when there is none. undefined (not "") keeps BCE seeing
// `None` so tests built without lesson material still report "Not evaluated,
// no source text". Teacher additionalContext is deliberately excluded: it is
// labeled NOT source material.
export function extractSourceText(materials: Material[] | undefined | null): string | undefined {
  // Legacy/raw configs can omit the schema-defaulted `materials` field, so a
  // missing or non-array value is the same as having no lesson text. One
  // guard here, not at call sites.
  if (!Array.isArray(materials)) return undefined;
  const chunks: string[] = [];
  for (const material of materials) {
    if (material.type === "url") {
      const pageText = (material.segments ?? [])
        .map((s) => s.text.trim())
        .filter((t) => t.length > 0)
        .join("\n\n");
      if (pageText) chunks.push(pageText);
    } else {
      const content = material.content?.trim();
      if (content) chunks.push(content);
    }
  }
  return chunks.length > 0 ? chunks.join("\n\n") : undefined;
}

// Canonical TestConfig for the generation/add-item wire. The saved config
// stores lesson text redundantly — customSkills duplicates materials, and a
// file material duplicates its extracted text in both `content` and
// `segments` — so the wire copy is stripped to one representation per lesson
// and stays inside the JSON body limit for the worst case (four 200K-char
// materials). buildSubmissionRequirements rebuilds the reference-material
// skills and source_text from the surviving fields, and file `segments` are
// only used client-side for source-anchor tooltips (read from the saved DB
// test, not the generation request). URL materials keep their segments — that
// is where their page text lives (`content` is the link string).
export function generationConfig(config: TestConfig): TestConfig {
  return {
    ...config,
    customSkills: undefined,
    materials: (config.materials ?? []).map((m) =>
      m.type === "file" ? { ...m, segments: undefined } : m,
    ),
  };
}

// Test Configuration Schema
export const testConfigSchema = z.object({
  audience: z.enum([
    "Elementary Students",
    "Middle School Students",
    "High School Students",
    "College Students",
    "Professional Adults",
    "Other"
  ]),
  readingLevel: z.enum(["K", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12", "Adult / workforce", "College", "Graduate"]),
  testLengthType: z.enum(["minutes", "items"]),
  testLengthValue: z.number().min(1).max(20),
  materials: z.array(materialSchema).default([]),
  // Lesson plans previously stored as raw strings; now use the same rich
  // shape as materials so file uploads on this section also survive
  // navigation. Strings from legacy data are migrated on load.
  //
  // Deprecated as a user-facing field — the IdentifyContent page no longer
  // exposes a lesson-plan upload zone; it was replaced by `additionalContext`
  // below. Kept here for backward compat with previously-saved configs.
  lessonPlans: z.array(z.union([materialSchema, z.string()])).default([]),

  // Free-text "additional context" the teacher can give the AI: pacing
  // constraints, tone, what to emphasize/avoid, accommodations, etc.
  // Plumbed into BuildBlueprint's prompt as a labeled instructions
  // section so Claude treats it as direction rather than source content.
  additionalContext: z.string().optional(),

  // NEW: Blacksmith integration fields (all optional for backward compatibility)
  // Subject is a free-form string so teachers can pick from the predefined
  // list OR type their own in the "Other (self entry)" mode of SubjectSelector.
  // Predefined values (math / science / reading / writing / social_studies /
  // computer_science) still work; anything else is treated as a custom subject.
  subject: z.string().optional(),  // defaults to "math" in API layer when omitted

  standardsState: z.enum(["common_core", "new_york"]).optional(),  // defaults to "common_core" in API layer

  // Custom Skills (Lesson Plans for AI context)
  customSkills: z.array(z.object({
    id: z.string(),
    name: z.string(),
    content: z.string(),
    extractedTopics: z.array(z.string()).optional(),
    suggestedStandards: z.array(z.string()).optional(),
  })).optional(),  // defaults to [] in API layer

  // Validator Configuration
  validatorConfig: z.object({
    preset: z.enum(["summative", "formative", "practice", "state_test", "custom"]).default("summative"),
    qualityThreshold: z.number().min(0).max(100).default(70),
    builtInWeights: z.record(z.number().min(0).max(2)).optional(),
  }).optional(),

  // Custom Validators
  customValidators: z.array(z.object({
    id: z.string(),
    name: z.string(),
    description: z.string(),
    script: z.string(),
    language: z.enum(["py", "js"]),
    required: z.boolean().default(true),
    weight: z.number().default(1.0),
  })).optional(),  // defaults to [] in API layer
});

export type TestConfig = z.infer<typeof testConfigSchema>;

// Standard Reference Schema
export const standardSchema = z.object({
  code: z.string(),
  domain: z.string(),
  description: z.string(),
  // Per-standard taxonomic distribution. Optional for backward compatibility
  // with standards loaded from the catalog. When set on a topic's standards,
  // the topic-level knowledge/understanding/higherOrderThinking counts are
  // the sum of the per-standard counts.
  knowledge: z.number().min(0).optional(),
  understanding: z.number().min(0).optional(),
  higherOrderThinking: z.number().min(0).optional(),
});

export type Standard = z.infer<typeof standardSchema>;

// Blueprint Topic Schema
export const blueprintTopicSchema = z.object({
  id: z.string(),
  name: z.string(),
  percentage: z.number().min(0).max(100),
  knowledge: z.number().min(0),
  understanding: z.number().min(0),
  higherOrderThinking: z.number().min(0),

  // NEW: Standards alignment (optional for backward compatibility)
  standards: z.array(standardSchema).optional(),

  // NEW: Difficulty distribution (Easy/Medium/Hard)
  difficulty: z.object({
    easy: z.number().min(0).default(0),
    medium: z.number().min(0).default(0),
    hard: z.number().min(0).default(0),
  }).optional(),

  // NEW: Additional context for AI generation
  context: z.string().optional(),

  // Source anchors derived from [[sN]] markers in the AI-returned
  // contentText. Drives the "Drawn from pages 3-5 of textbook.pdf"
  // tooltip on the blueprint page.
  sourceAnchors: z.array(sourceAnchorSchema).optional(),

  // True for topics the user adds by hand (no source material to anchor to).
  // Drives an "Added by you" note in place of the "Drawn from..." source line.
  addedManually: z.boolean().optional(),
});

export type BlueprintTopic = z.infer<typeof blueprintTopicSchema>;

// Blueprint Schema
export const blueprintSchema = z.object({
  topics: z.array(blueprintTopicSchema),
  prompt: z.string().optional(),
});

export type Blueprint = z.infer<typeof blueprintSchema>;

// Question Schema
export const questionSchema = z.object({
  id: z.string(),
  topicId: z.string(),
  level: z.enum(["Knowledge", "Understanding", "Higher-Order Thinking"]),
  stem: z.string(),
  // Variable-length options: Blacksmith generates 4 at base, but teachers may
  // add/remove options when editing (2–8). correctAnswer is bounds-checked
  // against answers.length by the refine below.
  answers: z.array(z.string()).min(2).max(8),
  correctAnswer: z.number().int().min(0),
  locked: z.boolean().default(false),
  stemLocked: z.boolean().default(false),
  answersLocked: z.boolean().default(false),

  // NEW: Blacksmith metadata (all optional for backward compatibility)
  standards: z.array(z.string()).optional(),
  difficulty: z.enum(["easy", "medium", "hard"]).optional(),
  difficultyScore: z.number().min(1).max(10).optional(),
  solutionSteps: z.array(z.string()).optional(),
  qualityScore: z.number().min(0).max(100).optional(),

  // NEW: Generation metadata
  blacksmithSessionId: z.string().optional(),
  provider: z.string().optional(),
  model: z.string().optional(),

  // Stable BCE task identifier — 1:1 with a displayed MCQ. Used to join
  // SOCRATES feedback regardless of display order (sort-safe). Optional for
  // backward compatibility with questions generated before this field existed.
  taskId: z.string().optional(),

  // Retained for backward compatibility only — the analyzer is the only place
  // answer options are ordered (Order Options Logically), and Waggle never
  // re-sorts a stored order, so no answerLetterMap is produced or consumed
  // anymore. Older saved tests may still carry one; it is ignored on display.
  // e.g. { "A": "C", "B": "A", "C": "D", "D": "B" } means BCE's original
  // option A is now displayed as option C, etc.
  answerLetterMap: z.record(z.string(), z.string()).optional(),

  // Persisted Socrates analysis for the question as it exists right now.
  // Written whenever a score is computed (auto-score after a regen, manual
  // re-score from the Edit modal, etc.) so it survives a page refresh and
  // reflects the current options — not the Blacksmith session's original
  // version. Shape is opaque here (matches SocratesQuestionData on the
  // client); kept as z.any() so older saved tests without it still validate.
  socratesScore: z.any().optional(),
  // True when an edit invalidated the prior score and no fresh score has
  // landed yet. Distinguishes "never scored" from "scored, but the result
  // is stale" so the mount-time session fetch can avoid overwriting a
  // voided item with whatever score is on the original session.
  socratesScoreVoided: z.boolean().optional(),

  // True when `answers` was built directly from BCE's ordered, final
  // SOCRATES-scored `options` field (transformToQuestion() in
  // server/blacksmith.ts), rather than the legacy Math.random() placement.
  // The analyzer is the only place answer options are ordered, and Waggle
  // never re-sorts a stored order, so the order carried on a question is the
  // order displayed. This flag just records the provenance of the order.
  optionsPreOrdered: z.boolean().optional(),

  // Server-side cross-item duplication flag set when a regenerate or add
  // produced an item that overlaps an existing item on the same test. Absent
  // when clean (or when the check never ran). Rendered as an amber banner on
  // the question card; the reasons are the server's human-readable findings.
  duplicate_warning: z
    .object({
      flagged: z.boolean(),
      reasons: z.array(z.string()),
    })
    .optional(),
}).refine((q) => q.correctAnswer < q.answers.length, {
  message: "correctAnswer must index into answers",
  path: ["correctAnswer"],
});

export type Question = z.infer<typeof questionSchema>;

// Complete Test Schema
export const testSchema = z.object({
  id: z.string(),
  name: z.string().default("Untitled Test"),
  config: testConfigSchema,
  blueprint: blueprintSchema,
  questions: z.array(questionSchema),
  directions: z.string().default(""),
  createdAt: z.string(),
  createdBy: z.number().optional(),
});

export type Test = z.infer<typeof testSchema>;

// Insert schemas for API
export const insertTestSchema = testSchema.omit({ id: true, createdAt: true });
export type InsertTest = z.infer<typeof insertTestSchema>;

// User schema (existing)
export const userSchema = z.object({
  id: z.string(),
  username: z.string(),
  password: z.string(),
});

export type User = z.infer<typeof userSchema>;

export const insertUserSchema = userSchema.omit({ id: true });
export type InsertUser = z.infer<typeof insertUserSchema>;
